import { useEffect, useState } from "react";
import { Button, Checkbox, Modal, Select } from "@legal-500/legal500-design-system";
import {
  LIST_KIND_LABELS,
  PartialBatchError,
  powerlistEntries,
  powerlistLists,
  type CopyEntriesResult,
  type Edition,
} from "../../../api/powerlist.service";
import { ErrorBanner } from "../../../components/ErrorBanner/ErrorBanner";
import LoadingState from "../../../components/LoadingState";
import { useQuery } from "../../../hooks/useApi";
import { FormError, errorMessage } from "../shared";
import styles from "../Powerlist.module.css";

export type SkippedCopy = { entryId: string; name: string; reason: string };

export type CopyOutcome = { copied: number; skipped: SkippedCopy[]; error: string | null };

type CopyEntriesModalProps = {
  editionId: string;
  otherEditions: Edition[];
  onClose: () => void;
  onCopied: (outcome: CopyOutcome) => void;
};

function entriesLabel(count: number): string {
  return `${count} ${count === 1 ? "entry" : "entries"}`;
}

export function CopyEntriesModal({
  editionId,
  otherEditions,
  onClose,
  onCopied,
}: CopyEntriesModalProps) {
  const editions = [...otherEditions].sort((a, b) => b.year - a.year);
  const [sourceId, setSourceId] = useState(editions[0]?.id ?? "");
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [copying, setCopying] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);

  const { data, error, refetch, isRefetching } = useQuery(async () => {
    const [lists, entries] = await Promise.all([
      powerlistLists.list(sourceId),
      powerlistEntries.listForEdition(sourceId),
    ]);
    return {
      sourceId,
      lists: [...lists].sort((a, b) => a.sortOrder - b.sortOrder),
      entries,
    };
  }, [sourceId]);

  useEffect(() => {
    if (data) setSelected(new Set(data.entries.map(entry => entry.id)));
  }, [data]);

  const source = data?.sourceId === sourceId ? data : null;
  const groups = source
    ? source.lists
        .map(list => ({
          list,
          entries: source.entries
            .filter(entry => entry.listId === list.id)
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" })),
        }))
        .filter(group => group.entries.length > 0)
    : [];
  const sourceEntryIds = groups.flatMap(group => group.entries.map(entry => entry.id));
  const chosen = sourceEntryIds.filter(id => selected.has(id));

  function toggle(id: string) {
    setSelected(previous => {
      const next = new Set(previous);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }

  function reasonFor(skip: CopyEntriesResult["skipped"][number]): string {
    if (skip.reason === "already_exists") return "already in this edition";
    const listId = source?.entries.find(entry => entry.id === skip.entryId)?.listId;
    const kind = source?.lists.find(list => list.id === listId)?.kind;
    return kind
      ? `this edition has no ${LIST_KIND_LABELS[kind]} list`
      : "this edition has no matching list";
  }

  async function copy() {
    setCopying(true);
    setCopyError(null);
    const outcomeOf = (result: CopyEntriesResult, error: string | null): CopyOutcome => ({
      copied: result.copied,
      skipped: result.skipped.map(skip => ({ ...skip, reason: reasonFor(skip) })),
      error,
    });
    try {
      const result = await powerlistEntries.copyFromEdition(editionId, sourceId, chosen);
      onCopied(outcomeOf(result, null));
    } catch (failure) {
      if (failure instanceof PartialBatchError) {
        onCopied(outcomeOf(failure.completed, failure.message));
        return;
      }
      setCopyError(errorMessage(failure));
      setCopying(false);
    }
  }

  return (
    <Modal isOpen onClose={onClose} title="Copy entries from another edition" size="mediumLarge">
      <div className={styles.form}>
        <FormError message={copyError} />
        <Select
          id="powerlist-copy-source"
          label="Copy from"
          value={sourceId}
          onChange={event => setSourceId(event.target.value)}
          options={editions.map(edition => ({ value: edition.id, label: edition.title }))}
          disabled={copying}
          fullWidth
        />
        <p className={styles.muted}>
          Copies are saved as drafts. Interview answers and badges aren't copied.
        </p>
        {error && <ErrorBanner error={error.message} onRetry={refetch} loading={isRefetching} />}
        {!source && !error && <LoadingState message="Loading entries..." />}
        {source && groups.length === 0 && (
          <p className={styles.muted}>This edition has no entries to copy.</p>
        )}
        {groups.length > 0 && (
          <>
            <div className={styles.actions}>
              <Button
                variant="text"
                size="small"
                disabled={copying}
                onClick={() => setSelected(new Set(sourceEntryIds))}
              >
                Select all
              </Button>
              <Button
                variant="text"
                size="small"
                disabled={copying}
                onClick={() => setSelected(new Set())}
              >
                Select none
              </Button>
              <p className={styles.muted} aria-live="polite">
                {entriesLabel(chosen.length)} selected
              </p>
            </div>
            <div className={styles.copyGroups}>
              {groups.map(({ list, entries }) => (
                <fieldset key={list.id} className={styles.copyGroup}>
                  <legend className={styles.sectionTitle}>{list.title}</legend>
                  {entries.map(entry => (
                    <Checkbox
                      key={entry.id}
                      label={
                        entry.companyName ? `${entry.name} · ${entry.companyName}` : entry.name
                      }
                      aria-label={`${entry.name} (${list.title})`}
                      checked={selected.has(entry.id)}
                      disabled={copying}
                      onChange={() => toggle(entry.id)}
                    />
                  ))}
                </fieldset>
              ))}
            </div>
          </>
        )}
        <div className={styles.formActions}>
          <Button variant="outlined" onClick={onClose} disabled={copying}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => void copy()}
            disabled={copying || chosen.length === 0}
          >
            {copying ? "Copying..." : `Copy ${entriesLabel(chosen.length)}`}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
